import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export type SubscriptionPlan = "free" | "pro";

interface SubscriptionData {
  plan: SubscriptionPlan;
  status?: string;
  currentPeriodEnd?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionData>({ plan: "free" });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      setIsLoading(true);

      // Check if user has a business profile with subscription info
      const { data: profile, error } = await supabase
        .from('business_profiles')
        .select('plan, stripe_customer_id, stripe_subscription_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        setSubscription({ plan: "free" });
        return;
      }

      if (profile) {
        setSubscription({
          plan: (profile.plan || "free") as SubscriptionPlan,
          stripeCustomerId: profile.stripe_customer_id || undefined,
          stripeSubscriptionId: profile.stripe_subscription_id || undefined,
        });
      } else {
        setSubscription({ plan: "free" });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription({ plan: "free" });
    } finally {
      setIsLoading(false);
    }
  };

  const upgradeToPro = async () => {
    if (!user) {
      setUpgradeError("Please sign in to upgrade");
      toast.error("Please sign in to upgrade");
      return;
    }

    console.log('[upgradeToPro] starting checkout for user:', user.id);
    setIsUpgrading(true);
    setUpgradeError(null);

    try {
      const returnUrl = `${window.location.origin}/upgrade/success`;
      const cancelUrl = `${window.location.origin}/upgrade/cancel`;
      console.log('[upgradeToPro] invoking stripe-connect createSubscription');

      // Call Stripe function to create checkout session for subscription
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'createSubscription',
          planType: 'pro',
          returnUrl,
          cancelUrl,
        }
      });

      console.log('[upgradeToPro] response — data:', data, 'error:', error);

      if (error) {
        // FunctionsHttpError: error.context is a Response object.
        // .json() is a method that must be awaited, not a property.
        let message = 'Failed to start upgrade process';
        try {
          const body = await (error as any)?.context?.json?.();
          console.error('[upgradeToPro] edge function error body:', body);
          if (body?.error) message = body.error;
          else if ((error as any)?.message) message = (error as any).message;
        } catch {
          // context.json() failed — try .text() as fallback
          try {
            const text = await (error as any)?.context?.text?.();
            console.error('[upgradeToPro] edge function error text:', text);
            if (text) message = text;
          } catch {
            if ((error as any)?.message) message = (error as any).message;
          }
        }
        throw new Error(message);
      }

      if (data?.url) {
        console.log('[upgradeToPro] redirecting to Stripe checkout:', data.url.substring(0, 40) + '...');
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        console.error('[upgradeToPro] no checkout URL in response:', data);
        throw new Error('No checkout URL returned. Please try again or contact support.');
      }
    } catch (error: any) {
      const msg = error.message || "Failed to start upgrade process. Please try again.";
      console.error('[upgradeToPro] failed:', msg, error);
      setUpgradeError(msg);
      toast.error(msg);
    } finally {
      setIsUpgrading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!subscription.stripeSubscriptionId) {
      toast.error("No active subscription found");
      return;
    }

    try {
      const { error } = await supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'cancelSubscription',
          subscriptionId: subscription.stripeSubscriptionId,
        }
      });

      if (error) {
        throw error;
      }

      toast.success("Subscription canceled successfully");
      await fetchSubscription(); // Refresh subscription data
    } catch (error: any) {
      console.error("Error canceling subscription:", error);
      toast.error(error.message || "Failed to cancel subscription");
    }
  };

  const getStripePortalUrl = async () => {
    if (!subscription.stripeCustomerId) {
      toast.error("No billing information found");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'createPortalSession',
          customerId: subscription.stripeCustomerId,
          returnUrl: `${window.location.origin}/dashboard/business-settings`,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error: any) {
      console.error("Error opening billing portal:", error);
      toast.error(error.message || "Failed to open billing portal");
    }
  };

  return {
    subscription,
    isLoading,
    isUpgrading,
    upgradeError,
    upgradeToPro,
    cancelSubscription,
    getStripePortalUrl,
    refetch: fetchSubscription,
  };
}
